// Die Mitspieler zwischen zwei Meldungen — **reine Darstellung**.
//
// Der Server schickt die Anwesenheit in seinem Tick, also fuenfmal pro Sekunde;
// gezeichnet wird sechzigmal. Wer die gemeldete Position hart setzt, zeigt ein
// fremdes Schiff, das bei voller Fahrt rund 50 Welteinheiten je Meldung
// springt — bei Kamerazoom 2 bis 4 sind das 100 bis 200 Bildpunkte. Genau das
// war die Beschwerde, und `NET-001` fuehrte es als offenen Punkt.
//
// Nichts hier behauptet ein Ergebnis: die Zwischenwerte sind eine Vermutung
// ueber fremde Schiffe, sie stehen in keinem Spielstand und beeinflussen keine
// Rechnung. Die Wahrheit bleibt die naechste Meldung des Servers (FG-32).
//
// Frameworkfrei wie der Rest von `net/` (Gate FG-18) — deshalb ist der Schritt
// ohne Browser pruefbar.

import { turnTowards } from '../core/math';
import type { PlayerPresence } from './protocol';

/**
 * Wie schnell die gezeichnete Position der gemeldeten folgt (1/Sekunde).
 *
 * Dasselbe Muster wie bei der Kamera: `1 - exp(-k * dt)` ist
 * bildratenunabhaengig, ein festes `t` je Frame waere es nicht.
 */
const FOLLOW = 9;

/** Wie schnell sich die gezeichnete Ausrichtung dreht (Bogenmass/Sekunde). */
const TURN = 7;

/**
 * Wie lange die Geschwindigkeit hoechstens fortgeschrieben wird.
 *
 * Ohne Deckel fliegt ein Mitspieler, dessen Meldung ausfaellt, ins Nichts —
 * und steht dann beim naechsten Paket doppelt so weit daneben, wie er ohne
 * jede Vorhersage gestanden haette. Zwei ausgefallene Ticks sind der
 * Spielraum, danach wartet sein Schiff.
 */
const LEAD_MAX = 0.45;

/** Sekunden, ueber die ein verschwundener Mitspieler ausblendet. */
const FADE = 0.4;

/** Ein Mitspieler, wie ihn die Szene zeichnet. */
export interface Peer {
  cid: string;
  pid: number;
  name: string;
  /** Schiffslevel — die Szene schaetzt daraus seine Ausbaustufe (`gfx/sprites`). */
  lvl: number;
  /** Gezeichnete Werte — geglaettet, nie gemeldet. */
  x: number;
  y: number;
  angle: number;
  /** Deckkraft: 1, solange er gemeldet wird, danach fallend. */
  alpha: number;
  /** Zuletzt gemeldete Werte. */
  targetX: number;
  targetY: number;
  targetAngle: number;
  vx: number;
  vy: number;
  /** Sekunden seit der letzten Meldung — Grundlage der Fortschreibung. */
  age: number;
  /** Fehlt er in der letzten Anwesenheitsliste? Dann blendet er aus. */
  gone: boolean;
}

/**
 * Alle sichtbaren Mitspieler, geglaettet.
 *
 * Zwei Eingaenge: `apply` nimmt eine Anwesenheitsnachricht entgegen, `step`
 * schreibt zwischen den Nachrichten fort. Beide **mutieren** — sie gehoeren
 * deshalb in den Schritt der Szene, nie in ihren Zeichenpfad (Gate FG-17).
 */
export class PeerField {
  private readonly peers = new Map<string, Peer>();

  /** Wie viele Mitspieler zuletzt gemeldet wurden — ohne die ausblendenden. */
  private reported = 0;

  all(): IterableIterator<Peer> {
    return this.peers.values();
  }

  get count(): number {
    return this.reported;
  }

  clear(): void {
    this.peers.clear();
    this.reported = 0;
  }

  /**
   * Eine Anwesenheitsnachricht uebernehmen — sie setzt nur **Ziele**.
   *
   * Ein neuer Mitspieler wird ohne Annaeherung gesetzt: sonst flöge er beim
   * ersten Erscheinen vom Ursprung her ein.
   */
  apply(players: PlayerPresence[]): void {
    const gesehen = new Set<string>();
    for (const player of players) {
      gesehen.add(player.cid);
      const bekannt = this.peers.get(player.cid);
      if (bekannt) {
        bekannt.name = player.name;
        bekannt.lvl = player.lvl;
        bekannt.targetX = player.x;
        bekannt.targetY = player.y;
        bekannt.targetAngle = player.a;
        bekannt.vx = player.vx;
        bekannt.vy = player.vy;
        bekannt.age = 0;
        bekannt.gone = false;
        bekannt.alpha = 1;
        continue;
      }
      this.peers.set(player.cid, {
        cid: player.cid, pid: player.pid, name: player.name, lvl: player.lvl,
        x: player.x, y: player.y, angle: player.a, alpha: 1,
        targetX: player.x, targetY: player.y, targetAngle: player.a,
        vx: player.vx, vy: player.vy, age: 0, gone: false,
      });
    }
    for (const peer of this.peers.values()) {
      if (!gesehen.has(peer.cid)) peer.gone = true;
    }
    this.reported = players.length;
  }

  /**
   * Zwischen zwei Meldungen zeichnen.
   *
   * Zwei Anteile, und beide werden gebraucht:
   *
   * 1. **Mitlaufen.** Ziel *und* gezeichnete Position wandern mit der
   *    gemeldeten Geschwindigkeit weiter. Ohne das haengt ein fahrender
   *    Mitspieler dauerhaft `v / FOLLOW` zurueck — bei Hoechstgeschwindigkeit
   *    eine ganze Schiffslaenge, sichtbar als Gummiband.
   * 2. **Nachlaufen.** Der Rest der Abweichung wird exponentiell aufgeholt.
   *    Das faengt Richtungswechsel ab, bei denen die Vorhersage danebenlag.
   */
  step(dt: number): void {
    const folge = 1 - Math.exp(-FOLLOW * dt);
    for (const peer of this.peers.values()) {
      const vorher = Math.min(peer.age, LEAD_MAX);
      peer.age += dt;
      const gefahren = Math.min(peer.age, LEAD_MAX) - vorher;
      // Erst beide mitlaufen lassen, **dann** die verbliebene Abweichung
      // aufholen. Andersherum zaehlte der Vorschub doppelt und der Mitspieler
      // liefe seiner eigenen Meldung davon.
      peer.targetX += peer.vx * gefahren;
      peer.targetY += peer.vy * gefahren;
      peer.x += peer.vx * gefahren;
      peer.y += peer.vy * gefahren;

      peer.x += (peer.targetX - peer.x) * folge;
      peer.y += (peer.targetY - peer.y) * folge;
      peer.angle = turnTowards(peer.angle, peer.targetAngle, TURN * dt);

      if (!peer.gone) continue;
      // Ausblenden statt verschwinden: wer den Umkreis streift oder dessen
      // Meldung einmal verworfen wurde, poppte sonst weg und wieder hin.
      peer.alpha -= dt / FADE;
      if (peer.alpha <= 0) this.peers.delete(peer.cid);
    }
  }
}
